import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { Skus } from '@helper/skus';
import { ProductMeta } from '@proto/store/api/common/common';
import { Product_SkuColor } from '@proto/store/api/store/product';
import { StockReminderModel } from '../stock_reminder.model';
import { AuthService } from '@service/auth.service';
import { Strings } from '@helper/strings';
import { Toast } from '@helper/toast';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';
import { SizeInfo } from '@helper/size-info';
import { AddressHelper } from '@helper/address';
import { LoginInputComponent } from '@common/login-input/login-input.component';
import { SafeStorageService } from '@service/safe-storage.service';

interface IDialogData {
  name: string;
  sku: string;
}

@Component({
  selector: 'app-stock-remind-dialog',
  templateUrl: './stock-remind-dialog.component.html',
  styleUrls: ['./stock-remind-dialog.component.scss'],
  imports: [
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSnackBarModule,
    FocusRemoverDirective,
    LoginInputComponent,
  ],
  providers: [StockReminderModel],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StockRemindDialogComponent {
  readonly phone = signal('');
  readonly isPhoneValid = computed(() => Strings.validPhoneNumber(this.transformedPhone()));
  readonly transformedPhone = computed(() => {
    const phone = this.phone();
    return phone.length < AddressHelper.phoneLength ? `0${phone}` : phone;
  });
  readonly phoneLength = AddressHelper.phoneLength;
  readonly data: IDialogData = inject(MAT_DIALOG_DATA);

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public authService: AuthService,
    public viewModel: StockReminderModel,
    private dialogRef: MatDialogRef<StockRemindDialogComponent>,
    private toast: MatSnackBar,
    storage: SafeStorageService
  ) {
    afterNextRender(() => this.phone.set(storage.getLoginPhone() || ''));
  }

  static open(
    dialog: MatDialog,
    meta?: ProductMeta,
    color?: Product_SkuColor,
    size?: SizeInfo
  ): MatDialogRef<StockRemindDialogComponent> {
    const data: IDialogData = {
      name: `${meta?.name}(${color?.color?.name ?? ''}-${size?.size.name})`,
      sku: Skus.skuId(meta?.productId ?? '', color?.id ?? '', size?.size.id ?? ''),
    };
    return dialog.open(StockRemindDialogComponent, {
      disableClose: true,
      data,
      width: '330px',
    });
  }

  addRemind(): void {
    if (!this.isPhoneValid()) {
      Toast.open(this.toast, $localize`請輸入正確手機號碼`);
      return;
    }
    this.viewModel.addStockReminder(this.destroyRef, this.data.sku, this.transformedPhone()).subscribe({
      next: () => {
        Toast.open(this.toast, $localize`加入通知成功`);
        this.dialogRef.close(true);
      },
      error: err => this.viewModel.setApiError(err),
    });
  }
}
